import React from "react";

const Home = () => {
  return (
    <div style={styles.container}>
      <div style={styles.pageContent}>

        {/* ================= HERO ROW ================= */}
        <div style={styles.heroRow}>

          {/* LEFT COLUMN */}
          <div style={styles.heroLeft}>
            <h1 style={styles.h1}>FentaSafe</h1>

            <h2 style={styles.h2}>
              AI-Driven Safety Intelligence &
              <br />
              Early Risk Detection Platform
            </h2>

            <p style={styles.subTag}>
              Purpose-built for <strong style={{color: "#60a5fa"}}>K-12 Schools</strong> and Public Safety Environments
            </p>

            <p style={styles.highlight}>
              From early signals to actionable intelligence — before risk becomes crisis.
            </p>

            <p style={styles.p}>
              FentaSafe is an AI-powered environmental risk detection platform designed
              for schools, public facilities, and high-risk environments. It analyzes
              multi-signal environmental data — PM2.5, VOCs, temperature, humidity,
              and contextual signals — to identify unusual patterns and alert trained
              staff to investigate. FentaSafe does not chemically identify fentanyl or
              any specific substance.
            </p>

            <p style={styles.p}>
              Built for public safety agencies, educational institutions, and emergency
              response organizations that need proactive intelligence, not reactive alerts.
            </p>

            {/* K-12 School Safety Callout */}
            <div style={styles.schoolCallout}>
              <span style={styles.schoolIcon}>🏫</span>
              <div>
                <strong style={{color: "#fbbf24"}}>Priority Focus: K-12 School Safety</strong>
                <p style={{margin: "4px 0 0", fontSize: "0.9rem", color: "#94a3b8"}}>
                  School districts may be able to fund technology like FentaSafe through
                  federal programs such as the COPS Office SVPP grant — funding amounts
                  are determined by the applicant district, not guaranteed per district.
                  Aligned with SchoolSafety.gov threat assessment frameworks.
                </p>
              </div>
            </div>

            {/* Development status — replaces unverified performance metrics */}
            <div style={styles.metricsRow}>
              <div style={styles.metric}>
                <span style={styles.metricValueSmall}>Home-based prototype testing in progress</span>
              </div>
              <div style={styles.metric}>
                <span style={styles.metricValueSmall}>Independent validation planned</span>
              </div>
            </div>

            <p style={styles.badge}>
              Air Quality • Early Warnings • Active Alerts • Situational Awareness
            </p>

            <h3 style={styles.sectionTitle}>🚨 The Public Safety Challenge</h3>

            <p style={styles.p}>
              Modern safety incidents begin as weak signals — environmental anomalies,
              substance exposure indicators, and contextual risk patterns that evolve
              into serious threats if not identified early. Traditional systems detect
              incidents only <em>after</em> escalation. FentaSafe is designed to help
              identify unusual patterns <strong>earlier</strong>.
            </p>

            <h3 style={styles.sectionTitle}>🌍 Where It Applies</h3>

            <p style={styles.p}>
              Schools • Campuses • Public Venues • Smart Cities • Transportation Hubs
            </p>

            <div style={styles.actions}>
              <a href="/demo" style={styles.primaryBtn}>View Live Demo</a>
              <a href="/contact" style={styles.secondaryBtn}>Contact Us</a>
            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div style={styles.heroRight}>
            <figure style={styles.imageFigure}>
              <img
                src={`${process.env.PUBLIC_URL}/images/fentasafe-command-center.png`}
                alt="FentaSafe Dashboard"
                style={styles.heroImage}
              />
              <figcaption style={styles.imageCaption}>
                Conceptual dashboard view — detailed metrics available in the live demo
              </figcaption>
            </figure>
          </div>

        </div>

        {/* FULL WIDTH SECTION */}
        <div style={styles.fullWidthSection}>
          <div style={styles.fullWidthInner}>
            <div style={styles.divider}></div>

            <div>
              <h3 style={styles.sectionTitle}>🧠 The FentaSafe Solution</h3>
              <ul style={styles.list}>
                <li><strong>Early Risk Detection</strong> — AI classifier identifies abnormal multi-signal patterns before escalation</li>
                <li><strong>Situational Awareness at Scale</strong> — Zone-based visibility with memory-driven incident similarity matching</li>
                <li><strong>Real-Time Decision Explanation</strong> — Per-decision signal attribution designed for real-time operation; formal latency benchmarks pending independent testing</li>
                <li><strong>Human-Supervised Intervention</strong> — Confidence-aware decision routing with human-in-the-loop governance</li>
                <li><strong>Adaptive Learning</strong> — Operator feedback continuously refines system sensitivity for your facility</li>
              </ul>

              <h3 style={styles.sectionTitle}>🛡️ What FentaSafe Is Not</h3>
              <ul style={styles.list}>
                <li>Not a surveillance or facial recognition system</li>
                <li>Not a law enforcement tracking platform</li>
                <li>Not a replacement for first responders</li>
                <li>Not a laboratory testing system — an early warning platform designed to identify potential exposure and risk scenarios</li>
                <li>Not a device that chemically identifies fentanyl or any specific substance</li>
              </ul>

              <h3 style={styles.sectionTitle}>⚙️ Development Status</h3>
              <ul style={styles.list}>
                <li>Environmental sensor prototype in active home-based testing</li>
                <li>Software platform and dashboard operational</li>
                <li>Seeking independent technical validation before pilot deployment</li>
                <li>Architecture designed with security and scalability in mind; not yet independently tested at scale</li>
              </ul>
            </div>

            <div>
              <h3 style={styles.sectionTitle}>🎯 Impact</h3>
              <p style={styles.p}>
                FentaSafe is designed to enable earlier detection, faster response, and
                improved situational awareness — with the goal of helping prevent critical
                incidents across public safety environments, pending independent validation.
              </p>

              <h3 style={styles.sectionTitle}>U.S. Strategic Alignment</h3>
              <p style={styles.p}>
                FentaSafe aligns with public safety innovation priorities including
                situational awareness, AI-driven threat identification, sensor fusion,
                and early warning systems for high-risk environments.
              </p>
              <p style={styles.p}>
                School districts may be able to fund technology like FentaSafe through
                federal programs such as the COPS Office School Violence Prevention
                Program (SVPP). Award amounts are determined per district through the
                application process; there is no guaranteed or fixed funding amount.
              </p>

              <h3 style={styles.sectionTitle}>🏫 Aligned with U.S. School Safety Frameworks</h3>
              <p style={styles.p}>
                FentaSafe is designed in alignment with U.S. school safety frameworks
                and reflects key domains highlighted in SchoolSafety.gov, including
                threat assessment and situational awareness.
              </p>

              <h3 style={styles.sectionTitle}>🔬 Research Interest</h3>
              <p style={styles.p}>
                FentaSafe has had early discussions with ICMR-NINE (National Institute
                of NCD Epidemiology) regarding potential future research collaboration.
                No formal validation partnership is currently in place.
                Indian Patent Application No. 202641022911 (Pending).
              </p>
            </div>

          </div>
        </div>

        {/* CAPABILITY STRIP */}
        <div style={styles.capabilityStrip}>
          <div style={styles.capabilityRow}>
            <div style={styles.capabilityCard}>
              <h4 style={{ color: "#22c55e" }}>Signal Intelligence</h4>
              <p>Multi-sensor environmental and contextual risk signal analysis with real-time classification.</p>
            </div>
            <div style={styles.capabilityCard}>
              <h4 style={{ color: "#eab308" }}>Predictive Early Warning</h4>
              <p>Real-time trend detection and escalation forecasting using live sensor history.</p>
            </div>
            <div style={styles.capabilityCard}>
              <h4 style={{ color: "#ef4444" }}>Adaptive Intervention</h4>
              <p>Human-supervised response workflows with real-time decision explanation and operator feedback learning.</p>
            </div>
            <div style={styles.capabilityCard}>
              <h4 style={{ color: "#38bdf8" }}>Network Intelligence</h4>
              <p>Memory-driven incident pattern matching and cross-facility safety insights.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;

const styles = {
  container: { minHeight: "100vh", background: "#0f172a", color: "#ffffff", padding: "40px 20px" },
  pageContent: { maxWidth: 1400, margin: "0 auto" },
  heroRow: { display: "grid", gridTemplateColumns: "1.05fr 1.2fr", gap: 48, alignItems: "start" },
  heroLeft: { maxWidth: "720px" },
  heroRight: { marginTop: -12 },
  imageFigure: { margin: 0, display: "flex", flexDirection: "column", alignItems: "center" },
  heroImage: { width: "100%", borderRadius: 16, boxShadow: "0 40px 90px rgba(0,0,0,0.65)" },
  imageCaption: { marginTop: 4, fontSize: 14, color: "#94a3b8", opacity: 0.75, fontStyle: "italic", textAlign: "center" },
  h1: { fontSize: "2.4rem", marginBottom: 10 },
  h2: { fontSize: "1.6rem", lineHeight: 1.4 },
  subTag: { fontSize: "1rem", opacity: 0.7, marginBottom: 12 },
  highlight: { fontSize: "1.1rem", fontWeight: 500, marginBottom: 16 },
  p: { marginTop: 12, lineHeight: 1.8, color: "#cbd5f5", maxWidth: "95%" },
  badge: { marginTop: 12, fontWeight: 500, color: "#93c5fd" },
  sectionTitle: { marginTop: 28, marginBottom: 10, fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0", letterSpacing: "0.3px" },
  list: { marginTop: 12, paddingLeft: 20, lineHeight: 1.8, color: "#cbd5f5" },
  actions: { display: "flex", gap: 16, marginTop: 20 },
  primaryBtn: { padding: "12px 24px", background: "#2563eb", color: "#fff", textDecoration: "none", borderRadius: 6, fontWeight: 600 },
  secondaryBtn: { padding: "12px 24px", border: "1px solid #2563eb", color: "#93c5fd", textDecoration: "none", borderRadius: 6 },
  schoolCallout: { display: "flex", gap: 14, alignItems: "flex-start", background: "rgba(251, 191, 36, 0.08)", border: "1px solid rgba(251, 191, 36, 0.25)", borderRadius: 8, padding: "14px 16px", marginTop: 16, marginBottom: 4 },
  schoolIcon: { fontSize: "1.6rem", lineHeight: 1, marginTop: 2 },
  metricsRow: { display: "flex", gap: 16, marginTop: 16, marginBottom: 8 },
  metric: { display: "flex", flexDirection: "column", alignItems: "flex-start", background: "rgba(37, 99, 235, 0.10)", border: "1px solid rgba(37, 99, 235, 0.25)", borderRadius: 8, padding: "10px 16px", minWidth: 100 },
  metricValue: { fontSize: "1.4rem", fontWeight: 700, color: "#60a5fa", lineHeight: 1.2 },
  metricValueSmall: { fontSize: "0.85rem", fontWeight: 600, color: "#60a5fa", lineHeight: 1.4 },
  metricLabel: { fontSize: "0.7rem", color: "#94a3b8", marginTop: 4, lineHeight: 1.3 },
  capabilityStrip: { width: "100vw", marginLeft: "calc(-50vw + 50%)", marginTop: 16, padding: "24px", background: "#020617" },
  capabilityRow: { maxWidth: 1400, margin: "0 auto", display: "flex", gap: 24 },
  capabilityCard: { flex: "1 1 0", background: "#020617", border: "1px solid #1e293b", borderRadius: 14, padding: "28px 24px", minHeight: 180 },
  fullWidthSection: { width: "100vw", marginLeft: "calc(-50vw + 50%)", marginTop: 32, padding: "48px 20px", background: "#020617", borderTop: "1px solid rgba(148, 163, 184, 0.1)", borderBottom: "1px solid rgba(148, 163, 184, 0.1)" },
  fullWidthInner: { maxWidth: 1400, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, position: "relative", alignItems: "start", paddingLeft: 10, paddingRight: 10 },
  divider: { position: "absolute", top: 0, bottom: 0, left: "50%", width: "1px", background: "rgba(148, 163, 184, 0.08)" },
};